import type {
  ProductInstance,
  SaunaDoor,
  Vec2,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import { SINGLE_DEFAULT } from './singleDoor';

export const DOOR_DEFAULT_WIDTH = 720;

let doorSeq = 0;
/** A stable-ish unique id for a new door. */
export function newDoorId(): string {
  return `door-${Date.now().toString(36)}-${doorSeq++}`;
}

/** What a door is built as: a plain leaf, a PT10 glazing (glass beside it) or a
 *  PT9 single door (docs/claude/glazings.md). */
export type DoorKind = 'plain' | 'pt10' | 'pt9';

/** THE rule for a door's kind, by its room's category, whether it is on a
 *  partition, and whether a pane touches it along its wall: an outer door with
 *  glass beside it in a sauna or steam room is a PT10 glazing, a steam room's
 *  outer door with none the PT9; anything else a plain leaf. The glazings'
 *  detection (roomGlazing.ts) and a new door (`defaultDoor`) both ask it. */
export function doorKindRule(category: ProductInstance['category'] | undefined, onPartition: boolean, glassBeside: boolean): DoorKind {
  if (onPartition) return 'plain';
  if (glassBeside && (category === 'sauna' || category === 'steamroom')) return 'pt10';
  return category === 'steamroom' ? 'pt9' : 'plain';
}

/** A fresh door's width on a run `runLen` long: the default, or 60% of a short
 *  run; a steam room's outer door (the PT9 single door) the drawing's 979, or
 *  80% of a short run. */
export function defaultDoorWidth(runLen: number, steamOuter: boolean): number {
  return steamOuter ? Math.max(300, Math.min(SINGLE_DEFAULT.width, runLen * 0.8)) : Math.max(300, Math.min(DOOR_DEFAULT_WIDTH, runLen * 0.6));
}

/** A fresh door, centred on its wall run, sized to the run (`defaultDoorWidth`).
 *  `edge`/`innerWallId` place it (default: outer edge 0). In a steam room
 *  (`category`) an outer door is the PT9's, its height the drawing's 2064 too. */
export function defaultDoor(width: number, host?: { edge?: number; innerWallId?: string }, category?: ProductInstance['category']): SaunaDoor {
  const steamOuter = doorKindRule(category, host?.innerWallId != null, false) === 'pt9';
  return {
    id: newDoorId(),
    offset: 0,
    hinge: 'left',
    width: defaultDoorWidth(width, steamOuter),
    edge: host?.innerWallId != null ? undefined : (host?.edge ?? 0),
    innerWallId: host?.innerWallId,
    ...(steamOuter ? { height: SINGLE_DEFAULT.height } : {}),
  };
}

/**
 * The room's doors as an array, migrating the legacy single `door` field into
 * `doors` on first access (idempotent). Every door gets a stable id and, for
 * outer-wall doors, an `edge` (legacy doors sat on edge 0 = the front wall).
 */
export function roomDoors(inst: ProductInstance): SaunaDoor[] {
  if (!inst.doors) {
    inst.doors = inst.door
      ? [{ ...inst.door, id: inst.door.id ?? newDoorId(), edge: inst.door.edge ?? 0 }]
      : [];
    inst.door = undefined;
  }
  return inst.doors;
}

/** The room's main (first outer-wall) door — the one the steam drain channel and
 *  the temporary front-wall dimension follow. Null when the room has none. */
export function mainRoomDoor(inst: ProductInstance): SaunaDoor | null {
  return roomDoors(inst).find((d) => d.innerWallId == null) ?? null;
}

/** Find a room's door by id (null if none / no match). */
export function doorById(inst: ProductInstance, id: string | null): SaunaDoor | null {
  if (!id) return null;
  return roomDoors(inst).find((d) => d.id === id) ?? null;
}

/** Flip a door's hinge side. */
export function flipDoor(door: SaunaDoor): void {
  door.hinge = door.hinge === 'left' ? 'right' : 'left';
}

// ---------------------------------------------------------------------------
// Legacy rectangular-frame helpers. In practice every room is polygonal (auto-
// migrated), so the poly path in polyRoom.ts is what draws/hits doors; these
// survive only for the vestigial `!isPolyRoom` branches and operate on a single
// passed door.
// ---------------------------------------------------------------------------

/** Keep a door within the front wall after a resize. */
export function clampDoor(inst: ProductInstance, door: SaunaDoor): void {
  door.width = Math.min(door.width, Math.max(300, inst.width - 2 * wallThicknessOf(inst)));
  door.offset = clampDoorOffset(inst, door, door.offset);
}

export function clampDoorOffset(inst: ProductInstance, door: SaunaDoor, x: number): number {
  const lim = Math.max(0, inst.width / 2 - wallThicknessOf(inst) - door.width / 2);
  return Math.max(-lim, Math.min(lim, x));
}

export function resizeDoorToJamb(inst: ProductInstance, door: SaunaDoor, side: 'left' | 'right', localX: number): void {
  const lim = inst.width / 2 - wallThicknessOf(inst);
  const MINW = 300;
  const L = door.offset - door.width / 2;
  const R = door.offset + door.width / 2;
  if (side === 'right') {
    const right = Math.max(L + MINW, Math.min(lim, localX));
    door.width = right - L;
    door.offset = (L + right) / 2;
  } else {
    const left = Math.min(R - MINW, Math.max(-lim, localX));
    door.width = R - left;
    door.offset = (left + R) / 2;
  }
  clampDoor(inst, door);
}

export interface DoorParts {
  jambL: Vec2;
  jambR: Vec2;
  hinge: Vec2;
  leafTip: Vec2;
  center: Vec2;
  radius: number;
  arcStart: number;
  arcEnd: number;
}

/** Door geometry in the instance's local frame (rectangular fallback). */
export function doorParts(inst: ProductInstance, door: SaunaDoor): DoorParts {
  const yWall = -inst.depth / 2;
  const half = door.width / 2;
  const jambL = { x: door.offset - half, y: yWall };
  const jambR = { x: door.offset + half, y: yWall };
  const hinge = door.hinge === 'left' ? jambL : jambR;
  const leafTip = { x: hinge.x, y: yWall - door.width };
  const arcStart = door.hinge === 'left' ? 270 : 180;
  const arcEnd = door.hinge === 'left' ? 360 : 270;
  return { jambL, jambR, hinge, leafTip, center: hinge, radius: door.width, arcStart, arcEnd };
}

export function doorFlipIconLocal(inst: ProductInstance, door: SaunaDoor): Vec2 {
  const yWall = -inst.depth / 2;
  const inset = Math.min(220, (inst.depth - wallThicknessOf(inst) * 2) / 2);
  return { x: door.offset, y: yWall + Math.max(60, inset) };
}

export function doorHitLocal(inst: ProductInstance, door: SaunaDoor, p: Vec2, margin: number): boolean {
  const yWall = -inst.depth / 2;
  const half = door.width / 2;
  return (
    p.x >= door.offset - half - margin &&
    p.x <= door.offset + half + margin &&
    p.y >= yWall - door.width - margin &&
    p.y <= yWall + margin
  );
}
